import api from "./axios";
import { Agent, AvailableAgent } from "types";
import { AgentStatus, AgentTypeValue } from "constants/agents";

// Request/Response types
export interface AssignAgentsRequest {
  agent_ids: string[];
}

export interface GetAgentsResponse {
  items: Agent[];
  total: number;
}

export interface GetAvailableAgentsResponse {
  items: AvailableAgent[];
}

export interface UpdateAgentRequest {
  agent_ids?: string[];
  [key: string]: any;
}

export interface AssignedAgent {
  agent_association_id: string;
  id: string;
  name: string;
  description: string;
  status: AgentStatus;
  agent_type: AgentTypeValue | string;
  created_at: string;
  updated_at: string;
}

export interface ImportAgentResponse {
  message_code: number;
  data: Data;
}

export interface Data {
  agent_id: string;
  agent_name: string;
  imported_documents_count: number;
  imported_guidances_count: number;
  imported_technical_notes_count: number;
  imported_suggestions_count: number;
  imported_workflows_count: number;
  imported_integrations_count: number;
}

// Agents API
export const agentsApi = {
  // Get all agents with pagination
  getAll: async (params?: {
    skip?: number;
    take?: number;
  }): Promise<GetAgentsResponse> => {
    const response = await api.get<GetAgentsResponse>("/agents", { params });
    return response.data;
  },

  // Get a single agent by ID
  getById: async (agentId: string): Promise<Agent> => {
    const response = await api.get<Agent>(`/agents/${agentId}`);
    return response.data;
  },

  // Update an agent
  update: async (agentId: string, data: UpdateAgentRequest): Promise<Agent> => {
    const response = await api.patch<Agent>(`/agents/${agentId}`, data);
    return response.data;
  },

  // Assign specialist agents to an orchestrator agent
  assignAgents: async (
    agentId: string,
    data: AssignAgentsRequest
  ): Promise<void> => {
    await api.post(`/agents/${agentId}/assign-agents`, data);
  },

  // Get available agents for assignment
  getAvailable: async (): Promise<GetAvailableAgentsResponse> => {
    const response = await api.get<GetAvailableAgentsResponse>(
      "/agents/available"
    );
    return response.data;
  },

  // Get assigned agents for an orchestrator agent
  getAssignedAgents: async (agentId: string): Promise<AssignedAgent[]> => {
    const response = await api.get<AssignedAgent[]>(
      `/agents/${agentId}/assign-agents`
    );
    return response.data;
  },

  // Remove a specialist agent from an orchestrator agent
  removeAssignedAgent: async (
    orchestratorId: string,
    agentId: string
  ): Promise<void> => {
    await api.delete(`/agents/${orchestratorId}/assign-agents/${agentId}`);
  },

  // Export agent to zip file
  export: async (
    agentId: string,
    agent_type: string,
    password?: string
  ): Promise<Blob> => {
    const params = {
      agent_type: agent_type,
      password: password,
    };
    const response = await api.get(`/agents/${agentId}/export`, {
      responseType: "blob",
      params,
    });
    return response.data;
  },

  // Import agent from zip file
  import: async (
    file: File,
    name?: string,
    password?: string
  ): Promise<{ task_id?: string } | ImportAgentResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    if (name) {
      formData.append("agent_name", name);
    }
    if (password) {
      formData.append("password", password);
    }
    const response = await api.post<{ task_id?: string } | ImportAgentResponse>(
      "/agents/import",
      formData
    );
    return response.data;
  },
};
